import axios, { type AxiosResponse } from "axios";
import { type ICampaignsDataService } from "../types/services";
import {
  type EmailTemplateRequest,
  type CampaignPayload,
  type FeedbackPageRequest,
  type CreateCampaignRunPayload,
} from "../types/models";
import { type CampaignOverview } from "../types/state";
import { type GetCampaignSendsRequest } from "../types/requests";
import KnownHttpHeaders from "features/Application/services/knownHttpHeaders";
import { type DistributedOperationResult } from "types";

const campaignsUrl = "/api/threat-defence/campaigns";
const campaignRunUrl = (id?: number) => `/api/threat-defence/campaigns/${id}/runs`;
const campaignsV2Url = "/api/v2/threat-defence/campaigns";
const campaignV2Url = (id?: number) => `${campaignsV2Url}/${id}`;
const emailTemplateV2Url = (id?: number) => `${campaignsV2Url}/${id}/email-template`;
const feedbackPageV2Url = (id?: number) => `${campaignsV2Url}/${id}/feedback-page`;

const instance: ICampaignsDataService = {
  getCampaignTypes() {
    return axios.get(`${campaignsUrl}/types`);
  },
  getTriggerTypes() {
    return axios.get(`${campaignsUrl}/trigger-types`);
  },
  getSubdomains() {
    return axios.get("/api/threat-defence/sub-domains");
  },

  async getPublishedCampaignsByIdsAsync(
    ids: string[],
    showPurchased: boolean,
  ): Promise<AxiosResponse<CampaignOverview[]>> {
    const config = {
      params: {
        filter: ids.map((x) => `(PrimaryId eq ${x})`).join(" or "),
      },
    };

    return axios.get(`${campaignsV2Url}/published`, config);
  },

  updateCampaignAsync(data: CampaignPayload) {
    return axios.put(`${campaignV2Url(data.id)}`, data);
  },
  updateEmailTemplateAsync(data: EmailTemplateRequest) {
    return axios.put(`${emailTemplateV2Url(data.id)}`, data);
  },
  updateFeedbackPageAsync(data: FeedbackPageRequest) {
    return axios.put(`${feedbackPageV2Url(data.id)}`, data);
  },
  getEmailTemplateContentUrlWithSasAsync: async (
    id: number,
    includeWritePermission: boolean = true,
  ): Promise<string> => {
    const contentResp = await axios.get<{ contentUrlWithSas: string }>(
      `/api/v2/threat-defence/campaigns/${id}/email-template/sas?includeWritePermission=${includeWritePermission}`,
    );

    return contentResp.data?.contentUrlWithSas;
  },
  getLandingPageContentUrlWithSasAsync: async (id: number, includeWritePermission: boolean = true): Promise<string> => {
    const contentResp = await axios.get<{ contentUrlWithSas: string }>(
      `/api/v2/threat-defence/campaigns/${id}/landing-page/sas?includeWritePermission=${includeWritePermission}`,
    );

    return contentResp.data?.contentUrlWithSas;
  },
  getFeedbackPageContentUrlWithSasAsync: async (
    id: number,
    includeWritePermission: boolean = true,
  ): Promise<string> => {
    const contentResp = await axios.get<{ contentUrlWithSas: string }>(
      `/api/v2/threat-defence/campaigns/${id}/feedback-page/video/sas?includeWritePermission=${includeWritePermission}`,
    );

    return contentResp.data?.contentUrlWithSas;
  },
  getPublishersFilterOptionsAsync() {
    return axios.get(`${campaignsV2Url}/publishers`);
  },
  getTagsFilterOptionsAsync(showPurchased: boolean) {
    return axios.get("/api/threat-defence/tags", { params: { type: "Label", showPurchased } });
  },
  getCampaign(id: number) {
    return axios.get(campaignV2Url(id));
  },
  getEmailTemplate(id: number) {
    return axios.get(emailTemplateV2Url(id));
  },
  getFeedbackPage(id: number) {
    return axios.get(feedbackPageV2Url(id));
  },
  createCampaignRun: async (id: number, data: CreateCampaignRunPayload) => {
    const response = await axios.post<DistributedOperationResult>(`${campaignRunUrl(id)}`, data);
    return response.data;
  },
  completeCampaignRun: (id: number) => {
    return axios.post(`${campaignsUrl}/runs/${id}/complete`, { campaignRunId: id });
  },
  cancelCampaignRun: (id: number) => {
    return axios.post(`${campaignsUrl}/runs/${id}/cancel`);
  },
  async getCampaignSends(id: number, request: GetCampaignSendsRequest) {
    const { data, headers } = await axios.get(`${campaignsUrl}/${id}/runs`, {
      params: request,
    });
    return {
      items: data,
      count: parseInt(headers[KnownHttpHeaders.RecordsCount]),
    };
  },
  duplicateCampaigns: (ids: number[]) => {
    return axios.post(`${campaignsUrl}/duplicate`, { ids });
  },
  deleteCampaigns: (ids: number[]) => {
    return axios.delete(`${campaignsV2Url}`, { data: { ids } });
  },
};

export default instance;
